import React, {FC, KeyboardEvent, useEffect, useRef, useState} from 'react'
import {CircleStop, Mail, Plus, Split} from 'lucide-react'
import {END_STEP_NAME, IHrProcessFlowNode, STEP_OUTCOME_LABEL, StepOutcome} from './HrProcessStepModel'

export interface FlowHandlers {
  busy: boolean
  onAdd: (parentId: string | null) => void
  onSplit: (parentId: string) => void
  onEnd: (parentId: string) => void
  onRename: (node: IHrProcessFlowNode, name: string) => void
  onDelete: (node: IHrProcessFlowNode) => void
  /** Adıma bağlı mail şablonunu düzenler */
  onMailTemplate: (node: IHrProcessFlowNode) => void
  /** Bitiş adımının sonucu (olumlu / olumsuz) */
  onOutcome: (node: IHrProcessFlowNode, outcome: StepOutcome) => void
}

type NodeProps = {
  node: IHrProcessFlowNode
  handlers: FlowHandlers
}

const ActionButton: FC<{title: string; disabled: boolean; onClick: () => void}> = ({
  title,
  disabled,
  onClick,
  children,
}) => (
  <button
    type='button'
    className='btn btn-icon btn-sm btn-light-primary'
    title={title}
    disabled={disabled}
    onClick={onClick}
  >
    {children}
  </button>
)

const DeleteButton: FC<{disabled: boolean; onClick: () => void}> = ({disabled, onClick}) => (
  <button
    type='button'
    className='btn btn-icon btn-danger hr-flow-delete'
    title='Sil'
    disabled={disabled}
    onClick={onClick}
  >
    <i className='bi bi-trash'></i>
  </button>
)

/** Başla / Bitiş dairesi; Bitiş adımına da mail şablonu bağlanabilir */
const FlowTerminal: FC<{
  label: string
  disabled?: boolean
  onDelete?: () => void
  node?: IHrProcessFlowNode
  handlers?: FlowHandlers
}> = ({label, disabled = false, onDelete, node, handlers}) => (
  <div
    className={`hr-flow-terminal ${
      node?.outcome ? `hr-flow-terminal--${node.outcome.toLowerCase()}` : ''
    }`}
  >
    {node && handlers && <MailButton node={node} handlers={handlers} />}
    {onDelete && <DeleteButton disabled={disabled} onClick={onDelete} />}
    <span>{label}</span>
  </div>
)

/** Sol üst köşedeki mail şablonu butonu; şablon bağlıysa dolu görünür */
const MailButton: FC<{node: IHrProcessFlowNode; handlers: FlowHandlers}> = ({node, handlers}) => {
  const hasTemplate = !!node.mailTemplateId
  return (
    <button
      type='button'
      className={`btn btn-icon hr-flow-mail ${hasTemplate ? 'btn-primary' : 'btn-light-primary'}`}
      title={
        hasTemplate
          ? `Otomatik mail: ${node.mailTemplateName || 'şablon seçili'}`
          : 'Adım maili tanımla'
      }
      disabled={handlers.busy}
      onClick={() => handlers.onMailTemplate(node)}
    >
      <Mail size={12} />
    </button>
  )
}

/** İçindeki adı yerinde düzenlenebilen adım dikdörtgeni */
const StepNode: FC<NodeProps> = ({node, handlers}) => {
  const [name, setName] = useState(node.name)
  const cancelledRef = useRef(false)

  useEffect(() => {
    setName(node.name)
  }, [node.name])

  const commit = () => {
    if (cancelledRef.current) {
      cancelledRef.current = false
      return
    }
    const trimmed = name.trim()
    if (!trimmed) {
      setName(node.name)
      return
    }
    if (trimmed !== node.name) handlers.onRename(node, trimmed)
  }

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      e.currentTarget.blur()
    } else if (e.key === 'Escape') {
      cancelledRef.current = true
      setName(node.name)
      e.currentTarget.blur()
    }
  }

  return (
    <div className='hr-flow-step'>
      <MailButton node={node} handlers={handlers} />
      <DeleteButton disabled={handlers.busy} onClick={() => handlers.onDelete(node)} />
      <textarea
        className='hr-flow-step__input'
        rows={2}
        maxLength={255}
        placeholder='Adım adı'
        title='Düzenlemek için tıklayın'
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKeyDown}
      />
    </div>
  )
}

/** Alt adımı olmayan adımın altındaki +, çift ok ve stop butonları */
const StepActions: FC<NodeProps> = ({node, handlers}) => (
  <div className='hr-flow-actions'>
    <ActionButton title='Adım Ekle' disabled={handlers.busy} onClick={() => handlers.onAdd(node.id)}>
      <Plus size={16} />
    </ActionButton>
    <ActionButton
      title='İki Adıma Dallan'
      disabled={handlers.busy}
      onClick={() => handlers.onSplit(node.id)}
    >
      <Split size={16} style={{transform: 'rotate(180deg)'}} />
    </ActionButton>
    <ActionButton title='Bitiş Ekle' disabled={handlers.busy} onClick={() => handlers.onEnd(node.id)}>
      <CircleStop size={16} />
    </ActionButton>
  </div>
)

/** Adıma bağlı mail şablonunun adı; akışta tek bakışta görünür */
const MailLabel: FC<{node: IHrProcessFlowNode}> = ({node}) =>
  node.mailTemplateId ? (
    <div className='hr-flow-mail-label' title={node.mailTemplateName || ''}>
      <i className='bi bi-envelope me-1'></i>
      {node.mailTemplateName || 'Otomatik mail'}
    </div>
  ) : null

/** Bitiş dairesinin altındaki sonuç seçimi; dashboard olumlu / olumsuz sayılarını buradan alır */
const OutcomeToggle: FC<NodeProps> = ({node, handlers}) => (
  <div className='hr-flow-outcome' role='group' aria-label='Bitiş sonucu'>
    {(['POSITIVE', 'NEGATIVE'] as StepOutcome[]).map((outcome) => (
      <button
        key={outcome}
        type='button'
        className={`hr-flow-outcome__option hr-flow-outcome__option--${outcome.toLowerCase()} ${
          node.outcome === outcome ? 'is-active' : ''
        }`}
        disabled={handlers.busy}
        onClick={() => node.outcome !== outcome && handlers.onOutcome(node, outcome)}
      >
        {STEP_OUTCOME_LABEL[outcome]}
      </button>
    ))}
    {!node.outcome && <span className='hr-flow-outcome__warning'>Sonuç seçiniz</span>}
  </div>
)

const FlowBranch: FC<NodeProps> = ({node, handlers}) => {
  if (node.stepType === 'END') {
    return (
      <>
        <FlowTerminal
          label={node.name || END_STEP_NAME}
          disabled={handlers.busy}
          onDelete={() => handlers.onDelete(node)}
          node={node}
          handlers={handlers}
        />
        <OutcomeToggle node={node} handlers={handlers} />
        <MailLabel node={node} />
      </>
    )
  }

  return (
    <>
      <StepNode node={node} handlers={handlers} />
      <MailLabel node={node} />
      {node.children.length > 0 ? (
        <FlowChildren nodes={node.children} handlers={handlers} />
      ) : (
        <StepActions node={node} handlers={handlers} />
      )}
    </>
  )
}

const FlowChildren: FC<{nodes: IHrProcessFlowNode[]; handlers: FlowHandlers}> = ({
  nodes,
  handlers,
}) => (
  <div className='hr-flow-children'>
    {nodes.map((child) => (
      <div key={child.id} className='hr-flow-child'>
        <span className='hr-flow-arrow' />
        <FlowBranch node={child} handlers={handlers} />
      </div>
    ))}
  </div>
)

export const HrProcessFlowCanvas: FC<{roots: IHrProcessFlowNode[]; handlers: FlowHandlers}> = ({
  roots,
  handlers,
}) => (
  <div className='hr-flow-canvas'>
    <div className='hr-flow'>
      <FlowTerminal label='Başla' />
      {roots.length > 0 ? (
        <FlowChildren nodes={roots} handlers={handlers} />
      ) : (
        <div className='hr-flow-actions'>
          <ActionButton
            title='İlk Adımı Ekle'
            disabled={handlers.busy}
            onClick={() => handlers.onAdd(null)}
          >
            <Plus size={16} />
          </ActionButton>
        </div>
      )}
    </div>
  </div>
)
